import {
    CHROMIUM_ZOOM_LEVELS,
    COMMON_PHYSICAL_SCREEN_WIDTHS,
    LAYOUT_BREAKPOINTS,
    MAX_RESIZE_EVENTS_PER_PAGE_LOAD,
    MAXIMISED_TOLERANCE_PX,
    OS_SCALE_LEVELS,
    PHYSICAL_WIDTH_TOLERANCE_PX,
    RESIZE_DEBOUNCE_MS,
    SCALE_SNAP_TOLERANCE,
    SMALLEST_BREAKPOINT_NAME,
    VIEWPORT_SNAPSHOT_EVENT,
} from './viewportTelemetry.data';

type AttributeValue = string | number | boolean | null;

// Structural subset of the New Relic BrowserAgent so this module stays easy to test
export type ViewportTelemetryAgent = {
    setCustomAttribute: (name: string, value: AttributeValue) => unknown;
    recordCustomEvent: (
        eventType: string,
        attributes?: Record<string, AttributeValue>,
    ) => unknown;
};

export type ScaleEstimateConfidence = 'high' | 'medium' | 'low' | 'unsupported';

export type DisplayMeasurements = {
    innerWidth: number;
    outerWidth: number;
    devicePixelRatio: number;
    screenWidth: number;
    screenAvailWidth: number;
    isChromium: boolean;
};

export type DisplayScaleEstimate = {
    rawZoomRatio: number | null;
    zoomEstimate: number | null;
    osScaleEstimate: number | null;
    physicalScreenWidthEstimate: number | null;
    isMaximisedEstimate: boolean;
    scaleEstimateConfidence: ScaleEstimateConfidence;
};

type ViewportSnapshot = DisplayScaleEstimate & {
    viewportWidth: number;
    viewportHeight: number;
    innerWidth: number;
    innerHeight: number;
    outerWidth: number;
    outerHeight: number;
    screenWidth: number;
    screenHeight: number;
    screenAvailWidth: number;
    screenAvailHeight: number;
    devicePixelRatio: number;
    layoutBreakpoint: string;
};

const SCALE_DECIMALS = 3;

const roundRatio = (value: number) => Number(value.toFixed(SCALE_DECIMALS));

const snapToLevel = (value: number, levels: Array<number>): number | null => {
    let closest: number | null = null;
    for (const level of levels) {
        const distance = Math.abs(value - level);
        if (
            distance / level <= SCALE_SNAP_TOLERANCE &&
            (closest === null || distance < Math.abs(value - closest))
        ) {
            closest = level;
        }
    }
    return closest;
};

/**
 * Best-effort split of devicePixelRatio into browser zoom and OS display scaling.
 * Only works in Chromium, where outerWidth is in OS-scaled pixels and unaffected by page zoom.
 * Firefox and Safari apply zoom to outer/screen dimensions, so the ratio is meaningless there.
 */
export const estimateDisplayScale = (
    measurements: DisplayMeasurements,
): DisplayScaleEstimate => {
    const isMaximisedEstimate =
        measurements.outerWidth >=
        measurements.screenAvailWidth - MAXIMISED_TOLERANCE_PX;

    if (
        !measurements.isChromium ||
        measurements.innerWidth <= 0 ||
        measurements.outerWidth <= 0
    ) {
        return {
            rawZoomRatio: null,
            zoomEstimate: null,
            osScaleEstimate: null,
            physicalScreenWidthEstimate: null,
            isMaximisedEstimate,
            scaleEstimateConfidence: 'unsupported',
        };
    }

    const rawZoomRatio = roundRatio(
        measurements.outerWidth / measurements.innerWidth,
    );
    const zoomEstimate = snapToLevel(rawZoomRatio, CHROMIUM_ZOOM_LEVELS);
    const osScaleEstimate = zoomEstimate
        ? snapToLevel(
              measurements.devicePixelRatio / zoomEstimate,
              OS_SCALE_LEVELS,
          )
        : null;
    const physicalScreenWidthEstimate = osScaleEstimate
        ? Math.round(measurements.screenWidth * osScaleEstimate)
        : null;
    const isCommonPhysicalWidth =
        physicalScreenWidthEstimate !== null &&
        COMMON_PHYSICAL_SCREEN_WIDTHS.some(
            (width) =>
                Math.abs(width - physicalScreenWidthEstimate) <=
                PHYSICAL_WIDTH_TOLERANCE_PX,
        );

    let scaleEstimateConfidence: ScaleEstimateConfidence = 'low';
    if (isCommonPhysicalWidth) {
        scaleEstimateConfidence = isMaximisedEstimate ? 'high' : 'medium';
    }

    return {
        rawZoomRatio,
        zoomEstimate,
        osScaleEstimate,
        physicalScreenWidthEstimate,
        isMaximisedEstimate,
        scaleEstimateConfidence,
    };
};

export const getLayoutBreakpoint = (width: number) =>
    LAYOUT_BREAKPOINTS.find((breakpoint) => width >= breakpoint.minWidth)
        ?.name ?? SMALLEST_BREAKPOINT_NAME;

// Collapses IDs (any segment containing a digit) so routes can be faceted, e.g. /job/123 -> /job/:id
export const normaliseRoute = (pathname: string) =>
    `/${pathname
        .split('/')
        .filter(Boolean)
        .map((segment) => (/\d/.test(segment) ? ':id' : segment))
        .join('/')}`;

const isChromium = () => 'userAgentData' in navigator;

const getViewportSnapshot = (): ViewportSnapshot => {
    const { documentElement } = document;
    const { screen } = window;

    return {
        viewportWidth: documentElement.clientWidth,
        viewportHeight: documentElement.clientHeight,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        screenWidth: screen.width,
        screenHeight: screen.height,
        screenAvailWidth: screen.availWidth,
        screenAvailHeight: screen.availHeight,
        devicePixelRatio: window.devicePixelRatio,
        // Media queries evaluate against innerWidth (including scrollbars)
        layoutBreakpoint: getLayoutBreakpoint(window.innerWidth),
        ...estimateDisplayScale({
            innerWidth: window.innerWidth,
            outerWidth: window.outerWidth,
            devicePixelRatio: window.devicePixelRatio,
            screenWidth: screen.width,
            screenAvailWidth: screen.availWidth,
            isChromium: isChromium(),
        }),
    };
};

const setViewportAttributes = (
    agent: ViewportTelemetryAgent,
    snapshot: ViewportSnapshot,
) => {
    agent.setCustomAttribute('viewportWidth', snapshot.viewportWidth);
    agent.setCustomAttribute('viewportHeight', snapshot.viewportHeight);
    agent.setCustomAttribute('devicePixelRatio', snapshot.devicePixelRatio);
    agent.setCustomAttribute('layoutBreakpoint', snapshot.layoutBreakpoint);
    agent.setCustomAttribute('zoomEstimate', snapshot.zoomEstimate);
    agent.setCustomAttribute('osScaleEstimate', snapshot.osScaleEstimate);
    agent.setCustomAttribute(
        'scaleEstimateConfidence',
        snapshot.scaleEstimateConfidence,
    );
};

/**
 * Records viewport/display data to New Relic:
 * - Custom attributes on all subsequent browser events, refreshed after resizes
 * - `ViewportSnapshot` events on start and after debounced resizes (capped per page load)
 * @returns A cleanup function that removes all listeners
 */
export const startViewportTelemetry = (agent: ViewportTelemetryAgent) => {
    let snapshot = getViewportSnapshot();
    let resizeCount = 0;
    let resizeTimeout: ReturnType<typeof setTimeout> | undefined;

    setViewportAttributes(agent, snapshot);
    agent.recordCustomEvent(VIEWPORT_SNAPSHOT_EVENT, {
        ...snapshot,
        trigger: 'load',
        route: normaliseRoute(window.location.pathname),
    });

    const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            const previous = snapshot;
            snapshot = getViewportSnapshot();

            const hasChanged =
                previous.viewportWidth !== snapshot.viewportWidth ||
                previous.viewportHeight !== snapshot.viewportHeight ||
                previous.devicePixelRatio !== snapshot.devicePixelRatio;
            if (!hasChanged) {
                return;
            }

            setViewportAttributes(agent, snapshot);
            if (resizeCount < MAX_RESIZE_EVENTS_PER_PAGE_LOAD) {
                resizeCount++;
                agent.recordCustomEvent(VIEWPORT_SNAPSHOT_EVENT, {
                    ...snapshot,
                    trigger: 'resize',
                    resizeCount,
                    route: normaliseRoute(window.location.pathname),
                    previousViewportWidth: previous.viewportWidth,
                    previousViewportHeight: previous.viewportHeight,
                    previousDevicePixelRatio: previous.devicePixelRatio,
                });
            }
        }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
        clearTimeout(resizeTimeout);
        window.removeEventListener('resize', handleResize);
    };
};
